/* eslint-disable @typescript-eslint/no-use-before-define */
/* eslint-disable no-restricted-syntax */
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { api, ApiResponse, throwError } from './BaseApi';

const cache = {
  SONAR_GROUPS: 'sonarGroups',
  GROUP_MESSAGES: 'groupMessages',
};
export { cache as SONAR_CACHE_KEYS };

const sonarInvite = async (props: any) => {
  const { episode_id, invitee_ids, group_name, user_id } = props;
  try {
    const res = await api.post('sonar/invite', {
      json: {
        episode_id,
        invitee_ids: invitee_ids.map((val: string) =>
          val
            .replace(new RegExp('ind__', 'g'), '')
            .replace(new RegExp('class__', 'g'), '')
        ),
        group_name,
        user_id,
      },
    });
    const { response, error } = await ApiResponse(res);
    if (error) throw error;
    return response;
  } catch (err) {
    return throwError(err, 'There was a problem creating the invite.');
  }
};

export const useSonarInvite = (episodeId: string) => {
  const queryClient = useQueryClient();
  return useMutation(sonarInvite, {
    onSuccess: () => {
      queryClient.invalidateQueries([cache.SONAR_GROUPS, episodeId]);
    },
  });
};

/* Join Group */
const joinGroup = async (props: any) => {
  const { group_id, invitee_ids = [] } = props;
  try {
    const res = await api.post('sonar/join-group', {
      json: {
        group_id,
        invitee_ids: invitee_ids.map((val: string) =>
          val
            .replace(new RegExp('ind__', 'g'), '')
            .replace(new RegExp('class__', 'g'), '')
        ),
      },
    });
    const { response, error } = await ApiResponse(res);
    if (error) throw error;
    return response;
  } catch (err) {
    return throwError(err, 'There was a problem joining the group.');
  }
};

export const useJoinGroup = (episodeId: string) => {
  const queryClient = useQueryClient();
  return useMutation(joinGroup, {
    onSuccess: () => {
      queryClient.refetchQueries([cache.SONAR_GROUPS, episodeId]);
    },
  });
};

export const useDeclineInvite = (episodeId: string) => {
  const queryClient = useQueryClient();
  return useMutation(
    async (groupId: string) => {
      try {
        const res = await api.post('sonar/decline-invite', {
          json: {
            group_id: groupId,
          },
        });
        const { response, error } = await ApiResponse(res);
        if (error) throw error;
        return response;
      } catch (err) {
        return throwError(err, 'There was a problem declining the invite.');
      }
    },
    {
      onSuccess: () => {
        queryClient.refetchQueries([cache.SONAR_GROUPS, episodeId]);
      },
    }
  );
};

/* Add Users to Group */
const addUsersToGroup = async (props: any) => {
  const { group_id, episode_id, invitee_ids } = props;
  try {
    const res = await api.post('sonar/invite', {
      json: {
        group_id,
        episode_id,
        invitee_ids: invitee_ids.map((val: string) =>
          val
            .replace(new RegExp('ind__', 'g'), '')
            .replace(new RegExp('class__', 'g'), '')
        ),
      },
    });
    const { response, error } = await ApiResponse(res);
    if (error) throw error;
    return response;
  } catch (err) {
    return throwError(err, 'There was a problem joining the group.');
  }
};
export const useAddUsersToGroup = (episodeId: string) => {
  const queryClient = useQueryClient();
  return useMutation(addUsersToGroup, {
    onSuccess: () => {
      queryClient.refetchQueries([cache.SONAR_GROUPS, episodeId]);
    },
  });
};

/* Mute/Unmute */
const setMute = async (props: any) => {
  const { group_id, group_member_id, status } = props;
  try {
    const res = await api.post('sonar/mute', {
      json: {
        group_id,
        group_member_id,
        status,
      },
    });
    const { response, error } = await ApiResponse(res);
    if (error) throw error;
    return response;
  } catch (err) {
    return throwError(err, 'There was a problem joining the group.');
  }
};

export const useMute = (episodeId: string, groupId: string) => {
  const queryClient = useQueryClient();
  return useMutation(setMute, {
    onSuccess: () => {
      queryClient.invalidateQueries([cache.SONAR_GROUPS, episodeId]);
      queryClient.invalidateQueries([cache.GROUP_MESSAGES, groupId]);
    },
  });
};

const getSonarGroups = async (episodeId: string, userId: string) => {
  try {
    const res = await api.post('sonar/groups', {
      json: {
        episode_id: episodeId,
        user_id: userId,
      },
    });
    const { response, error } = await ApiResponse(res);
    if (error) throw error;
    return response.groups;
  } catch (err) {
    return throwError(err, 'There was a problem fetching your sonar groups.');
  }
};

export const useSonarGroups = (episodeId: string, userId: string) => {
  return useQuery(
    [cache.SONAR_GROUPS, episodeId],
    () => getSonarGroups(episodeId, userId),
    {
      enabled: !!episodeId && !!userId,
    }
  );
};

const getGroupMessages = async (groupId: string) => {
  const messages: any = await api.get(`sonar/${groupId}/messages`).json();
  return messages;
};
export function useSonarGroupMessages(groupId: string) {
  return useQuery(
    [cache.GROUP_MESSAGES, groupId],
    () => getGroupMessages(groupId),
    {
      enabled: !!groupId,
      staleTime: 5000,
    }
  );
}

const submitMessage = async (props: any) => {
  const { group_id, user_id, message } = props;
  try {
    const res: any = await api.post(`sonar/${group_id}/messages`, {
      json: {
        user_id,
        message,
      },
    });
    const { response, error } = await ApiResponse(res);
    if (error) throw error;
    return response;
  } catch (err) {
    console.log('err!', err);
    return throwError(err, 'There was a problem submitting your comment.');
  }
};
export const useNewSonarGroupMessage = (groupId: string) => {
  const queryClient = useQueryClient();
  return useMutation(submitMessage, {
    onSuccess: (res) => {
      queryClient.invalidateQueries([cache.GROUP_MESSAGES, groupId]);
    },
  });
};
// export function useResponseComments(responseId: string) {
//   return useQuery(
//     [cache.RESPONSE_COMMENTS, responseId],
//     () => getCommentsByResponseId(responseId),
//     {
//       staleTime: 10000,
//     }
//   );
// }
// const getResponsesByPromptId = async (promptId: string) => {
//   // const response = await api.get('users/123');
//   const responses: any = await api.get(`responses/${promptId}`).json();
//   console.log('api call', promptId, responses);
//   return responses;
// };

// export function usePromptResponses(promptId: string) {
//   return useQuery([cache.PROMPT_RESPONSES, promptId], () =>
//     getResponsesByPromptId(promptId)
//   );
// }

// const getCommentsByResponseId = async (responseId: string) => {
//   // const response = await api.get('users/123');
//   const comments: any = await api.get(`response/${responseId}/comments`).json();
//   return comments;
// };
// export function useResponseComments(responseId: string) {
//   return useQuery(
//     [cache.RESPONSE_COMMENTS, responseId],
//     () => getCommentsByResponseId(responseId),
//     {
//       staleTime: 10000,
//     }
//   );
// }

// const addResponseComment = async (props: any) => {
//   const { response_id, user_id, comment } = props;
//   const res: any = await api
//     .post(`response/${response_id}/comments`, {
//       json: {
//         user_id,
//         comment,
//       },
//     })
//     .json();
//   return res;
// };
// export const useAddResponseComment = () => {
//   const queryClient = useQueryClient();
//   return useMutation(addResponseComment, {
//     onSuccess: (res) => {
//       queryClient.invalidateQueries([cache.RESPONSE_COMMENTS, res.response_id]);
//       queryClient.invalidateQueries(cache.PROMPT_RESPONSES);
//     },
//   });
// };

// /* Responses */
// const submitResponse = async (props: any) => {
//   const { user_id, prompt_id, type, text } = props;
//   try {
//     const res: any = await api
//       .post(`responses/${prompt_id}`, {
//         json: {
//           user_id,
//           type,
//           text,
//         },
//       })
//       .json();

//     const { response, error } = ApiResponse(res);
//     if (error) throw error;
//     return response;
//   } catch (err) {
//     return throwError(err, 'There was a problem submitting your response.');
//   }
// };
// export const useSubmitResponse = (promptId: string) => {
//   const queryClient = useQueryClient();
//   return useMutation(submitResponse, {
//     onSuccess: () => {
//       queryClient.invalidateQueries([cache.PROMPT_RESPONSES, promptId]);
//     },
//   });
// };
